// The dark card under the Dhātu lab's table: the chosen cell used in a sentence (अहं पढामि।), its meaning, tags for
// person, number, tense and root, audio, and the way to the step-by-step formation.

import type { FC, ReactNode } from 'react'
import type { GridCell } from '@/prakrit/components/grammar/grammar-grid'
import type { Num, Person, Tense } from '@/prakrit/lib/grammar/types'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import { Workflow } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NUMBER_LABEL, PERSON_LABEL, TENSE_LABEL } from '@/prakrit/lib/grammar/labels'
import { Bi } from '@/prakrit/components/common/bi'
import { PrakritText } from '@/prakrit/components/common/prakrit-word'
import { AudioButton } from '@/prakrit/components/common/audio-button'
import { cellGloss, sentenceMeaning, sentenceOf } from '@/prakrit/components/grammar/dhatu/meaning'

interface SentenceCardProps {
    root: LabRoot
    tense: Tense
    person: Person
    number: Num
    cell: GridCell
    showAlt: boolean
    onHowBuilt: () => void
    className?: string
}

const TAG = 'hindi inline-flex items-center rounded-full border border-background/15 bg-background/10 px-2.5 py-1 text-xs leading-none text-background/85'

export const SentenceCard: FC<SentenceCardProps> = ({ root, tense, person, number, cell, showAlt, onHowBuilt, className }): ReactNode => {
    const form = cell.core[0] ?? ''
    const sentence = form ? sentenceOf(root, tense, person, number, 'pu', form) : ''
    const meaning = sentenceMeaning(root, tense, person, number)
    const gloss = cellGloss(root, tense, person, number)
    const alts = showAlt ? cell.alt : []
    const others = cell.core.slice(1)

    if (!sentence) return null

    return (
        <section
            aria-label='वाक्य में प्रयोग · Used in a sentence'
            className={cn('flex flex-col gap-5 rounded-3xl bg-foreground px-5 py-6 text-background shadow-card sm:flex-row sm:items-center sm:justify-between sm:px-7', className)}
        >
            <div className='flex min-w-0 flex-col gap-2.5'>
                <p lang='pra' className='font-prakrit text-[clamp(1.875rem,1.35rem+1.9vw,2.625rem)] font-bold leading-snug text-background'>
                    <PrakritText text={sentence} />
                </p>
                {meaning ? (
                    <Bi hi={meaning.hi} en={meaning.en} className='gap-0.5 text-base text-background/85' enClassName='text-background/70' />
                ) : (
                    <Bi hi={gloss.hi} en={gloss.en} className='gap-0.5 text-sm text-background/80' enClassName='text-background/65' />
                )}
                {(others.length > 0 || alts.length > 0) && (
                    <p className='text-sm text-background/70'>
                        <span lang='hi' className='hindi'>{others.length > 0 ? 'दूसरा रूप' : 'विकल्प रूप'}: </span>
                        <span lang='pra' className='font-prakrit font-semibold text-background/90'>{[...others, ...alts].join(', ')}</span>
                        <span lang='en' data-ex='en' className='text-xs'> ({others.length > 0 ? 'also correct' : 'variant forms'})</span>
                    </p>
                )}
                <ul aria-label='व्याकरण · Grammar' className='flex flex-wrap gap-1.5 pt-1'>
                    <li className={TAG}>{PERSON_LABEL[person].hi}</li>
                    <li className={TAG}>{NUMBER_LABEL[number].hi}</li>
                    <li className={TAG}>{TENSE_LABEL[tense].hi}</li>
                    <li className={TAG}>{root.root} धातु</li>
                </ul>
            </div>

            <div className='flex shrink-0 flex-wrap items-center gap-2 sm:flex-col sm:items-end'>
                <AudioButton text={sentence} size='sm' className='[&>button]:h-11 [&>button]:px-4 [&>button]:text-sm' />
                <button
                    type='button'
                    onClick={onHowBuilt}
                    className='inline-flex min-h-tap items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90'
                >
                    <Workflow className='size-4' aria-hidden='true' />
                    <span lang='hi' className='hindi'>कैसे बना?</span>
                    <span lang='en'>How is it built?</span>
                </button>
            </div>
        </section>
    )
}
